import type { AuthProvider } from "@refinedev/core";
import { supabase } from "../lib/supabaseClient";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

export interface AdminInfo {
  id: string;
  email: string;
  name: string;
}

async function verifyAdminAccess(accessToken: string): Promise<AdminInfo> {
  const response = await fetch(`${API_URL}/api/v1/auth/me`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (response.status === 403 || response.status === 401) {
    throw new Error("Brak uprawnień administratora");
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || "Nie udało się zweryfikować uprawnień administratora");
  }

  return response.json() as Promise<AdminInfo>;
}

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error || !data.session) {
      throw new Error(error?.message || "Login failed");
    }

    try {
      const admin = await verifyAdminAccess(data.session.access_token);
      localStorage.setItem("access_token", data.session.access_token);
      localStorage.setItem("refresh_token", data.session.refresh_token);
      localStorage.setItem("admin_info", JSON.stringify(admin));
      return { success: true };
    } catch (verifyError) {
      await supabase.auth.signOut();
      throw verifyError;
    }
  },

  logout: async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (token) {
        await fetch(`${API_URL}/api/v1/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      await supabase.auth.signOut();
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("admin_info");
    }

    return { success: true };
  },

  onError: async () => {
    return { error: undefined };
  },

  check: async () => {
    const { data } = await supabase.auth.getSession();
    const session = data.session;
    if (!session?.access_token) {
      return { authenticated: false, redirectTo: "/login" };
    }

    localStorage.setItem("access_token", session.access_token);
    if (session.refresh_token) {
      localStorage.setItem("refresh_token", session.refresh_token);
    }

    try {
      const admin = await verifyAdminAccess(session.access_token);
      localStorage.setItem("admin_info", JSON.stringify(admin));
      return { authenticated: true };
    } catch {
      await supabase.auth.signOut();
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("admin_info");
      return { authenticated: false, redirectTo: "/login" };
    }
  },

  getIdentity: async () => {
    const adminInfo = localStorage.getItem("admin_info");
    if (adminInfo) {
      try {
        return JSON.parse(adminInfo) as AdminInfo;
      } catch (error) {
        console.error("Failed to parse admin info:", error);
      }
    }
    return null;
  },

  getPermissions: async () => {
    return null;
  },
};

export default authProvider;
